/**
 * Кабинет хозяйства на каноне: `/my/today`, `/my/fields`, `/my/report/*`,
 * `/my/history`.
 *
 * Читает — общий канонический корпус (`adapter.ts`), пишет — серверными
 * операциями `api/client.ts` с CSRF. Локальный слой записи (`auth/overlay`) на
 * этих маршрутах не участвует вовсе: черновик в браузере и отчёт в корпусе —
 * разные утверждения, и хранить их под одной формой значит однажды показать
 * хозяйству, что оно отчиталось, когда сервер запись не принял.
 *
 * Повторный отчёт после спора пишется КАНОНОМ, а не поверх прежнего: у
 * обязательства с действующим отчётом стоит `amend=true` — новая версия, а
 * исходная запись остаётся в базе. Переписывать отчёт нельзя: спор о том, что
 * произошло на поле, разрешается новой записью, а не правкой старой.
 */

import { ApiError, api, type QueryParams } from '../../../api/client';
import { corpus, forgetCorpus, readCanonDataset, type RouteAdapter } from './corpus';
import { canonParams, numericId } from './canon';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  notRecordId: {
    id: 'data.cabinet.notRecordId',
    defaultMessage: 'это не идентификатор записи базы района',
    description: 'Причина отказа записи отчёта (строчная фраза в списке отклонённых): работа не из базы района, а демонстрационная',
  },
  workNotFound: {
    id: 'data.cabinet.workNotFound',
    defaultMessage: 'работа не найдена в срезе сервера',
    description: 'Причина, почему отчёт не записан: работа не нашлась в текущем срезе (строчная фраза)',
  },
});

/** Причина отказа для работы без идентификатора записи базы района. */
export function notRecordIdReason(): string {
  return tr(m.notRecordId);
}

/** Один отчёт по контурному обязательству. */
export interface ReportRow {
  obligationId: string;
  /** Дата выполнения. */
  on: string;
  note: string | null;
  /**
   * Ось хода (журнал №290): «выполнено» или «в процессе». По умолчанию —
   * «выполнено»: так форма работала до оси.
   */
  progress?: 'done' | 'in_progress';
}

/**
 * Что сервер принял и что отверг.
 *
 * Отказ приходит СТРОКОЙ, а не молчанием: «ни одна запись не сохранена» без
 * причины неотличимо от «ничего не нажали».
 */
export interface WriteOutcome {
  written: string[];
  rejected: { id: string; reason: string }[];
}

function refusal(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  return String(cause);
}

/**
 * Отчёт хозяйства по выбранным контурам.
 *
 * Пишется по одному обязательству, а не массово: форма даёт хозяйству снять
 * отдельные контуры, и массовый ввод накрыл бы снятые тоже.
 */
export async function reportContours(
  rows: readonly ReportRow[],
  params: QueryParams,
): Promise<WriteOutcome> {
  const standing = (await corpus(params)).standing;
  const written: string[] = [];
  const rejected: { id: string; reason: string }[] = [];

  for (const row of rows) {
    const id = numericId(row.obligationId);
    if (id === null) {
      rejected.push({ id: row.obligationId, reason: notRecordIdReason() });
      continue;
    }
    try {
      await api.reportObligation(
        id,
        {
          progress: row.progress ?? 'done',
          occurred_on: row.on,
          // Действующий отчёт не переписывается: уточнение идёт версией.
          amend: standing.has(row.obligationId),
          note: row.note ?? '',
        },
        canonParams(params),
      );
      written.push(row.obligationId);
    } catch (cause) {
      rejected.push({ id: row.obligationId, reason: refusal(cause) });
    }
  }

  if (written.length > 0) forgetCorpus();
  return { written, rejected };
}

/**
 * Отчёт за всю работу хозяйства одним жестом — вход диспетчера.
 *
 * «От имени» клиент НЕ объявляет: кто вносит запись и за кого, сервер знает из
 * сессии и из владельца обязательства. Позволить прислать это полем значило бы
 * разрешить подписаться чужим именем.
 */
export async function reportFarmWork(
  workId: string,
  body: { on: string; note: string },
  params: QueryParams,
): Promise<{ ok: true; applied: number; skipped: number } | { ok: false; reason: string }> {
  const map = (await corpus(params)).farmObligationByWork;
  const id = map.get(workId);
  if (id === undefined) return { ok: false, reason: tr(m.workNotFound) };
  try {
    const answer = await api.reportFarmObligation(
      id,
      {
        progress: 'done',
        occurred_on: body.on,
        area_basis: 'applicable',
        note: body.note,
      },
      canonParams(params),
    );
    forgetCorpus();
    return {
      ok: true,
      applied: answer.applied,
      skipped: answer.skipped_more_advanced + answer.skipped_same_level,
    };
  } catch (cause) {
    return { ok: false, reason: refusal(cause) };
  }
}

/** Адаптер кабинета: маршруты `/my/*` и наборы канонического корпуса. */
export const cabinetAdapter: RouteAdapter = {
  routes: ['/my/today', '/my/fields', '/my/history', '/my/report/:cycleId/:workId'],
  read: (name, params) => readCanonDataset(name, params),
};
